import Vue from 'vue'

const RESET = 'RESET'
const SET_TEMPLATES = 'SET_TEMPLATES'
const SET_TEMPLATES_LOADING = 'SET_TEMPLATES_LOADING'
const SET_ITEM = 'SET_ITEM'
const SET_ITEMS = 'SET_ITEMS'
const SET_ITEMS_LOADING = 'SET_ITEMS_LOADING'
const REORDER_ITEMS = 'REORDER_ITEMS'
const SET_IS_REORDERING_ITEMS = 'SET_IS_REORDERING_ITEMS'

const BASE_ENDPOINT = 'api/LandingPageStaticContent'
const TEMPLATES_ENDPOINT = `${BASE_ENDPOINT}/Templates`
const SINGLE_ITEM_ENDPOINT = `${BASE_ENDPOINT}/Item`
const ITEMS_ENDPOINT = `${BASE_ENDPOINT}/Items`
const ITEMS_REORDER_ENDPOINT = `${ITEMS_ENDPOINT}/Reorder`

const INVALID_TEMPLATE_ID = -1

function initialState() {
  return {
    templates: {
      data: [],
      isLoading: false,
    },
    items: {
      data: [],
      isLoading: false,
    },
    isReorderingItems: false,
  }
}

export default {
  namespaced: true,
  state: initialState,
  actions: {
    async getStaticContentTemplates({ commit, state }, { forced, tenantName }) {
      const useCache = state.templates.data.length > 0 && !forced
      if (useCache) {
        return
      }
      const config = { params: {} }
      if (tenantName && tenantName != '') {
        config.params.tenant = tenantName
      }
      commit(SET_TEMPLATES_LOADING, true)
      try {
        const res = await Vue.axios.get(TEMPLATES_ENDPOINT, config)
        commit(SET_TEMPLATES, res.data)
      } finally {
        commit(SET_TEMPLATES_LOADING, false)
      }
    },
    setStaticContentTemplates({ commit }, templates) {
      commit(SET_TEMPLATES, templates)
    },
    async getStaticContentItems({ commit, state }, { forced, tenantName }) {
      const useCache = state.items.data.length > 0 && !forced
      if (useCache) {
        return
      }
      const config = { params: {} }
      if (tenantName && tenantName != '') {
        config.params.tenant = tenantName
      }
      commit(SET_ITEMS_LOADING, true)
      try {
        const res = await Vue.axios.get(ITEMS_ENDPOINT, config)
        commit(SET_ITEMS, res.data)
        commit(REORDER_ITEMS)
      } finally {
        commit(SET_ITEMS_LOADING, false)
      }
    },
    setStaticContentItems({ commit, state }, { items, forced }) {
      if (forced == null) {
        forced = true
      }
      if (forced || state.items.data.length == 0) {
        commit(SET_ITEMS, items)
      }
    },
    async addStaticContentItem({ commit }, { data, tenantName }) {
      commit(SET_ITEMS_LOADING, true)
      const config = { params: {} }
      if (tenantName && tenantName != '') {
        config.params.tenant = tenantName
      }
      try {
        const res = await Vue.axios.post(SINGLE_ITEM_ENDPOINT, data, config)
        commit(SET_ITEM, res.data)
        commit(REORDER_ITEMS)
      } finally {
        commit(SET_ITEMS_LOADING, false)
      }
    },
    async updateStaticContentItem({ commit }, { data, tenantName }) {
      commit(SET_ITEMS_LOADING, true)
      const config = { params: {} }
      if (tenantName && tenantName != '') {
        config.params.tenant = tenantName
      }
      try {
        const res = await Vue.axios.put(SINGLE_ITEM_ENDPOINT, data, config)
        commit(SET_ITEM, res.data)
        commit(REORDER_ITEMS)
      } finally {
        commit(SET_ITEMS_LOADING, false)
      }
    },
    async deleteStaticContentItem({ commit, dispatch }, { contentItemId, tenantName }) {
      commit(SET_ITEMS_LOADING, true)
      const config = { params: { contentItemId } }
      if (tenantName && tenantName != '') {
        config.params.tenant = tenantName
      }
      try {
        await Vue.axios.delete(SINGLE_ITEM_ENDPOINT, config)
        await dispatch('getStaticContentItems', { tenantName, forced: true })
      } finally {
        commit(SET_ITEMS_LOADING, false)
      }
    },
    async reorderStaticContentItems({ commit }, { data, tenantName }) {
      commit(SET_ITEMS_LOADING, true)
      commit(SET_IS_REORDERING_ITEMS, true)
      const config = { params: {} }
      if (tenantName && tenantName != '') {
        config.params.tenant = tenantName
      }
      try {
        const res = await Vue.axios.put(ITEMS_REORDER_ENDPOINT, data, config)
        commit(SET_ITEMS, res.data)
      } finally {
        commit(SET_IS_REORDERING_ITEMS, false)
        commit(SET_ITEMS_LOADING, false)
      }
    },
    async moveStaticContentItemUp({ commit, state, dispatch }, { contentItemId, tenantName }) {
      if (contentItemId == null) {
        return
      }
      const itemIds = state.items.data.map(x => x.Id)
      const itemIndex = itemIds.findIndex(x => x == contentItemId)
      const itemNotInIds = itemIndex < 0
      const itemAlreadyAtTheTop = itemIndex == 0
      if (itemNotInIds || itemAlreadyAtTheTop) {
        return
      }
      const spotAboveItem = itemIndex - 1
      itemIds[spotAboveItem] = itemIds.splice(itemIndex, 1, itemIds[spotAboveItem])[0]
      await dispatch('reorderStaticContentItems', { tenantName, data: { contentItemIds: itemIds } })
    },
    async moveStaticContentItemDown({ commit, state, dispatch }, { contentItemId, tenantName }) {
      if (contentItemId == null) {
        return
      }
      const itemIds = state.items.data.map(x => x.Id)
      const itemIndex = itemIds.findIndex(x => x == contentItemId)
      const itemNotInIds = itemIndex < 0
      const itemAlreadyAtTheBottom = itemIndex == (itemIds.length - 1)
      if (itemNotInIds || itemAlreadyAtTheBottom) {
        return
      }
      const spotBelowItem = itemIndex + 1
      itemIds[spotBelowItem] = itemIds.splice(itemIndex, 1, itemIds[spotBelowItem])[0]
      await dispatch('reorderStaticContentItems', { tenantName, data: { contentItemIds: itemIds } })
    },
  },
  mutations: {
    [RESET](state) {
      const s = initialState()
      Object.keys(s).forEach(key => {
        state[key] = s[key]
      })
    },
    [SET_TEMPLATES](state, templates) {
      Vue.set(state.templates, 'data', templates)
    },
    [SET_TEMPLATES_LOADING](state, isLoading) {
      Vue.set(state.templates, 'isLoading', isLoading)
    },
    [SET_ITEM](state, item) {
      if (item == null || item.Id == null) {
        return
      }
      const itemIndex = state.items.data.findIndex(x => x.Id == item.Id)
      if (itemIndex >= 0) {
        state.items.data.splice(itemIndex, 1, item)
      } else {
        const newItems = [...state.items.data, item]
        Vue.set(state.items, 'data', newItems)
      }
    },
    [SET_ITEMS](state, items) {
      if (items == null) {
        return
      }
      Vue.set(state.items, 'data', items)
    },
    [SET_ITEMS_LOADING](state, isLoading) {
      Vue.set(state.items, 'isLoading', isLoading)
    },
    [REORDER_ITEMS](state) {
      const reorderedItems = state.items.data.sort((x, y) => x.Ordinal - y.Ordinal)
      Vue.set(state.items, 'data', reorderedItems)
    },
    [SET_IS_REORDERING_ITEMS](state, isReordering) {
      Vue.set(state, 'isReorderingItems', isReordering)
    },
  },
  getters: {
    hasStaticContentItems: state => state.items.data.length > 0,
    getTemplateIdByName: state => templateName => {
      const template = state.templates.data.find(x => x.DisplayName == templateName)
      return (template && template.Id) || INVALID_TEMPLATE_ID
    },
    nextOrdinal: state => state.items.data.length,
  },
}

export { INVALID_TEMPLATE_ID }